import { Component, OnDestroy, OnInit, Optional, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import moment from 'moment';
import { LocalizationService } from 'src/app/services/localization.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-form-studio-working-hours',
  templateUrl: './form-studio-working-hours.component.html',
  styleUrls: ['./form-studio-working-hours.component.css']
})
export class FormStudioWorkingHoursComponent implements OnInit, OnDestroy {
  studioWorkingHoursForm: FormGroup;
  configFieldName = 'field_day_view_reference';
  private lastUpdateDateTimeFormat = 'MMM D, YYYY h:mm A';
  /**
   * IMPORTANT: Day indices in this component follow backend convention
   * where Monday=0, Tuesday=1, ..., Sunday=6
   * 
   * This differs from JavaScript/FullCalendar convention where
   * Sunday=0, Monday=1, ..., Saturday=6
   * 
   * Calendar components must map these indices when displaying working hours.
   * See mapBackendDayToCalendarDay() function in calendar components.
   */
  days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  readonly allDays = [0, 1, 2, 3, 4, 5, 6];
  readonly weekdays = [0, 1, 2, 3, 4];
  hours = Array.from({ length: 96 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, '0')}${minute.toString().padStart(2, '0')}`;
  });

  actionType: 'create' | 'edit' = 'create';
  showProgressSpinner = false;
  successMessage = '';
  errorMessage = '';

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService,
    private localizationService: LocalizationService,
    private studioConfigService: StudioConfigService,
    @Optional() public dialogRef: MatDialogRef<FormStudioWorkingHoursComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any
  ) {
    this.studioConfigSubscription = this.studioConfigService.getStudioConfig().subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);
      this.lastUpdateDateTimeFormat = formats.calendarDateFormat.startsWith('DD') ? 'D MMM YYYY h:mm A' : 'MMM D, YYYY h:mm A';
    });
  }

  // Studio config is app-lifetime; released so the closed form can be collected.
  private studioConfigSubscription: Subscription;

  ngOnDestroy(): void {
    this.studioConfigSubscription?.unsubscribe();
  }

  ngOnInit(): void {
    this.studioWorkingHoursForm = this.fb.group({
      field_studio_working_hours: this.fb.array([])
    });

    this.loadStudioWorkingHours();
  }

  get workingHours(): FormArray {
    return this.studioWorkingHoursForm.get('field_studio_working_hours') as FormArray;
  }

  addWorkingHour(day: number, dayDelta: number, allDay: boolean, startHours: string, endHours: string, comment: string, closed = false) {
    const group = this.fb.group({
      day: [day],
      day_delta: [dayDelta],
      all_day: [allDay],
      starthours: [startHours, Validators.required],
      endhours: [endHours, Validators.required],
      comment: [comment],
      // UI-only: a closed day is saved by leaving its row out entirely, which is
      // how office_hours (and StudioHoursChecker) represent a closed day.
      closed: [closed]
    });
    if (closed) {
      this.setTimesEnabled(group, false);
    }
    this.workingHours.push(group);
  }

  loadStudioWorkingHours() {
    this.showProgressSpinner = true;

    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${this.configFieldName}`)
      .subscribe(
        (response) => {
          const workingHours = response?.['field_studio_working_hours'];
          if (workingHours && workingHours.length) {
            // Clear the existing form array
            this.workingHours.clear();

            // One row per day; a day with no stored row is closed.
            this.days.forEach((day, index) => {
              const stored = workingHours.find(workingHour => +workingHour.day === index);
              if (stored) {
                this.addWorkingHour(
                  index,
                  stored.day_delta ?? 0,
                  !!stored.all_day,
                  stored.starthours.toString().padStart(4, '0'),
                  stored.endhours.toString().padStart(4, '0'),
                  stored.comment ?? ''
                );
              } else {
                this.addWorkingHour(index, 0, false, '1300', '2200', '', true);
              }
            });

            this.actionType = 'edit';
          } else {
            // Add default working hours if no data is loaded
            this.days.forEach((day, index) => {
              this.addWorkingHour(index, 0, false, '1300', '2200', '');
            });
          }
          this.showProgressSpinner = false;
        },
        (error) => {
          LoggerService.error('Error loading studio working hours:', error);
          this.errorMessage = 'Failed to load studio working hours.';
          this.showProgressSpinner = false;
        }
      );
  }

  saveStudioWorkingHours() {
    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    const postData = {
      config_field_name: this.configFieldName,
      config_data: this.buildConfigData()
    };

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', postData)
      .subscribe(
        (response) => {
          LoggerService.log('Studio working hours saved successfully:', response);
          this.successMessage = 'Studio working hours saved successfully.';
          this.showProgressSpinner = false;
          
          // Close dialog immediately after successful save if in dialog mode
          if (this.dialogRef) {
            this.dialogRef.close(true);
          }
        },
        (error) => {
          LoggerService.error('Error saving studio working hours:', error);
          this.errorMessage = 'Failed to save studio working hours.';
          this.showProgressSpinner = false;
        }
      );
  }

  updateStudioWorkingHours() {
    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    const postData = {
      config_field_name: this.configFieldName,
      config_data: this.buildConfigData()
    };

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', postData)
      .subscribe(
        (response) => {
          LoggerService.log('Studio working hours updated successfully:', response);
          this.successMessage = 'Studio working hours updated successfully.';
          this.showProgressSpinner = false;
          
          // Close dialog immediately after successful update if in dialog mode
          if (this.dialogRef) {
            this.dialogRef.close(true);
          }
        },
        (error) => {
          LoggerService.error('Error updating studio working hours:', error);
          this.errorMessage = 'Failed to update studio working hours.';
          this.showProgressSpinner = false;
        }
      );
  }

  convertTo12HourFormat(time: string): string {
    if (typeof time !== 'string') {
      return '';
    }

    const [hours, minutes] = [time.slice(0, 2), time.slice(2)];
    const period = +hours >= 12 ? 'PM' : 'AM';
    const formattedHours = +hours % 12 || 12;
    return `${formattedHours}:${minutes} ${period}`;
  }

  resetRow(index: number) {
    const workingHour = this.workingHours.at(index);
    this.setClosed(workingHour, false);
    workingHour.patchValue({
      starthours: '1300',
      endhours: '2200',
      all_day: false,
      comment: ''
    });
  }

  // Copies one day's hours (or its closed state) onto the given days.
  copyToDays(index: number, targetDays: number[]) {
    const source = (this.workingHours.at(index) as FormGroup).getRawValue();
    this.workingHours.controls.forEach((workingHour, i) => {
      if (i === index || !targetDays.includes(workingHour.get('day').value)) {
        return;
      }
      this.setClosed(workingHour, source.closed);
      workingHour.patchValue({
        starthours: source.starthours,
        endhours: source.endhours,
        all_day: source.all_day,
        comment: source.comment
      });
    });
  }

  onAllDayChange(index: number) {
    const workingHour = this.workingHours.at(index);
    const allDay = workingHour.get('all_day').value;

    if (allDay) {
      workingHour.patchValue({
        starthours: '0000',
        endhours: '2345'
      });
    } else {
      workingHour.patchValue({
        starthours: '1300',
        endhours: '2200'
      });
    }
  }

  // New helper methods for the redesigned form
  getHourRange(start: number, end: number): string[] {
    return this.hours.slice(start, end);
  }

  private setTimesEnabled(workingHour: AbstractControl, enabled: boolean) {
    ['starthours', 'endhours'].forEach(name => {
      const control = workingHour.get(name);
      enabled ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false });
    });
  }

  setClosed(workingHour: AbstractControl, closed: boolean) {
    workingHour.patchValue({ closed });
    this.setTimesEnabled(workingHour, !closed);
  }

  // Closed days are left out so the stored field only holds open days.
  private buildConfigData() {
    return {
      field_studio_working_hours: this.workingHours.getRawValue()
        .filter(workingHour => !workingHour.closed)
        .map(({ closed, ...workingHour }) => workingHour)
    };
  }

  getLastUpdateTime(): string {
    // In a real app, this would come from the backend
    // For now, return current date/time or "Never" if creating new
    if (this.actionType === 'edit') {
      return moment().format(this.lastUpdateDateTimeFormat);
    }
    return 'Never';
  }
}
